import type { OrderState } from '../../pages/CreateSalesOrderPage';
import { Button } from '@/shared/components/ui/Button';
import { Loader2 } from 'lucide-react';

interface Props {
  state: OrderState;
  updateState: (updater: (prev: OrderState) => OrderState) => void;
  onSave: (action: 'draft' | 'new' | 'continue') => void;
  isSaving: boolean;
}

export function AdvancedOptionsSection({ state, updateState, onSave, isSaving }: Props) {
  const toggleOption = (field: keyof OrderState['advancedOptions']) => {
    updateState(s => ({
      ...s,
      advancedOptions: {
        ...s.advancedOptions,
        [field]: !s.advancedOptions[field as keyof typeof s.advancedOptions]
      }
    }));
  };

  return (
    <div className="mt-6 overflow-hidden rounded-card border border-border bg-card shadow-xs">
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-label-sm uppercase tracking-wider text-on-surface-variant">Advanced options</h2>
      </div>
      
      <div className="grid grid-cols-1 gap-6 p-4 md:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="so-hsn-view" className="block text-body-sm font-medium text-on-surface">Select HSN column view</label>
            <select
              id="so-hsn-view"
              className="block h-10 w-full rounded-lg border border-input bg-surface px-3 text-data-md text-on-surface focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={state.advancedOptions.hsnView || 'Default'}
              onChange={(e) => updateState(s => ({ ...s, advancedOptions: { ...s.advancedOptions, hsnView: e.target.value } }))}
            >
              <option value="Default">Default</option>
              <option value="Hidden">Hidden</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="so-unit-display" className="block text-body-sm font-medium text-on-surface">Display unit as</label>
            <select
              id="so-unit-display"
              className="block h-10 w-full rounded-lg border border-input bg-surface px-3 text-data-md text-on-surface focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={state.advancedOptions.displayUnitAs || 'Merge with quantity'}
              onChange={(e) => updateState(s => ({ ...s, advancedOptions: { ...s.advancedOptions, displayUnitAs: e.target.value } }))}
            >
              <option value="Merge with quantity">Merge with quantity</option>
              <option value="Separate column">Separate column</option>
            </select>
          </div>
        </div>

        <div className="space-y-4">
          <ToggleRow label="Show tax summary in invoice" checked={state.advancedOptions.showTaxSummary || false} onChange={() => toggleOption('showTaxSummary')} />
          <ToggleRow label="Hide place/country of supply" checked={state.advancedOptions.hidePlaceOfSupply || false} onChange={() => toggleOption('hidePlaceOfSupply')} />
          <ToggleRow label="Show HSN summary in invoice" checked={state.advancedOptions.showHsnSummary || false} onChange={() => toggleOption('showHsnSummary')} />
          <ToggleRow label="Add original images in line items" checked={state.advancedOptions.addOriginalImages || false} onChange={() => toggleOption('addOriginalImages')} />
          <ToggleRow label="Show thumbnails in separate column" checked={state.advancedOptions.showThumbnails || false} onChange={() => toggleOption('showThumbnails')} />
        </div>

        <div className="space-y-4">
          <ToggleRow label="Show description in full width" checked={state.advancedOptions.showDescriptionFullWidth || false} onChange={() => toggleOption('showDescriptionFullWidth')} />
          <ToggleRow label="Hide subtotal for group items" checked={state.advancedOptions.hideSubtotalForGroupItems || false} onChange={() => toggleOption('hideSubtotalForGroupItems')} />
          <ToggleRow label="Show SKU in Sales Order" checked={state.advancedOptions.showSku || false} onChange={() => toggleOption('showSku')} />
          <ToggleRow label="Show Serial Numbers in Sales Order" checked={state.advancedOptions.showSerialNumbers || false} onChange={() => toggleOption('showSerialNumbers')} />
          <ToggleRow label="Display Batch Details in columns" checked={state.advancedOptions.displayBatchDetails || false} onChange={() => toggleOption('displayBatchDetails')} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-border bg-muted px-4 py-3">
        <Button variant="outline" onClick={() => onSave('continue')} disabled={isSaving}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save & Continue
        </Button>
        <Button variant="outline" onClick={() => onSave('new')} disabled={isSaving}>
          Save & Create New
        </Button>
        <Button variant="outline" onClick={() => onSave('draft')} disabled={isSaving}>
          Save As Draft
        </Button>
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="group flex cursor-pointer items-center justify-between gap-4">
      <span className="text-body-sm text-on-surface transition-colors group-hover:text-on-surface">{label}</span>
      <div className="relative shrink-0">
        <input type="checkbox" aria-label={label} className="sr-only peer" checked={checked} onChange={onChange} />
        <div className="h-5 w-9 rounded-full bg-outline peer-checked:bg-primary peer-checked:after:translate-x-full peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-outline-variant after:bg-surface after:content-[''] motion-safe:after:transition-all"></div>
      </div>
    </label>
  );
}
